import React, { useState, useRef, useEffect, useMemo } from "react";
import domtoimage from "dom-to-image-more";
import axios from "axios";
import "../css/theme.css";
import "../css/addCreditor.css"; // reuse ac styles

const GenerateImage = ({
  creditors,
  debitors,
  onTransactionSubmit,
  prefillData = { creditorId: 1, debitorId: 8 },
  onClose,
}) => {
  const [hasQrCode, setHasQrCode] = useState(null);
  const [creditor, setCreditor] = useState(prefillData.creditorId || "");
  const [debitor, setDebitor] = useState(prefillData.debitorId || "");
  const [amount, setAmount] = useState(prefillData.amount || "");
  const [qrCode, setQrCode] = useState(null);
  const [modeOfTransfer, setModeOfTransfer] = useState(
    prefillData.mode_of_transfer || ""
  );
  const [bankName, setBankName] = useState(prefillData.bank_name || "");
  const [branch, setBranch] = useState(prefillData.branch_name || "");
  const [ifscCode, setIfscCode] = useState(prefillData.ifsc_code || "");
  const [accountHolderName, setAccountHolderName] = useState(
    prefillData.account_holder_name || ""
  );
  const [accountNumber, setAccountNumber] = useState(
    prefillData.account_number || ""
  );
  const [parentTransactionId] = useState(
    prefillData.parent_transaction_id || null
  );
  const [customTransactionId, setCustomTransactionId] = useState(
    prefillData.custom_transaction_id || ""
  );

  const [imageGenerated, setImageGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const imageRef = useRef(null);

  const selectedCreditor = useMemo(
    () => creditors.find((c) => c.id === Number(creditor)),
    [creditor, creditors]
  );
  const selectedDebitor = useMemo(
    () => debitors.find((d) => d.id === Number(debitor)),
    [debitor, debitors]
  );

  // Auto-fill amount for special creditor
  useEffect(() => {
    if (selectedCreditor?.is_special && selectedCreditor.committed_amount) {
      setAmount(selectedCreditor.committed_amount);
    }
  }, [selectedCreditor]);

  const handleQrCodeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Invalid file format. Please upload an image.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setQrCode(reader.result);
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    if (!creditor || !debitor) {
      setError("Creditor and Debitor are required.");
      return false;
    }
    if (!selectedCreditor || !selectedDebitor) {
      setError("Selected Creditor or Debitor not found in list.");
      return false;
    }

    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      setError("Enter a valid positive amount.");
      return false;
    }

    if (hasQrCode === null) {
      setError("Please select whether you have a QR/Check image.");
      return false;
    }

    if (hasQrCode && !qrCode) {
      setError("Please upload the QR/Check image.");
      return false;
    }

    if (!hasQrCode && !modeOfTransfer.trim()) {
      setError("Enter mode of transfer.");
      return false;
    }

    if (!bankName.trim()) {
      setError("Bank name is required.");
      return false;
    }
    if (!branch.trim()) {
      setError("Branch is required.");
      return false;
    }

    if (!ifscCode.trim()) {
      setError("IFSC code is required.");
      return false;
    }
    // basic IFSC validation (Indian format)
    const ifscPattern = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;
    if (!ifscPattern.test(ifscCode.trim())) {
      setError("Please enter a valid IFSC code.");
      return false;
    }

    if (!accountHolderName.trim()) {
      setError("Account holder name is required.");
      return false;
    }

    if (!accountNumber.trim()) {
      setError("Account number is required.");
      return false;
    }
    const accPattern = /^[0-9]{6,20}$/; // simple numeric range check
    if (!accPattern.test(accountNumber.trim())) {
      setError("Account number should be 6–20 digits.");
      return false;
    }

    setError("");
    return true;
  };

  const ensureTransactionId = async () => {
    if (customTransactionId) return customTransactionId;

    try {
      const res = await axios.post("/api/check_transaction_id.php", {
        parent_id: parentTransactionId || undefined,
      });
      if (res.data.success && res.data.next_id) {
        setCustomTransactionId(res.data.next_id);
        return res.data.next_id;
      }
      throw new Error(res.data.message || "Transaction ID generation failed");
    } catch (err) {
      setError("Transaction ID error: " + err.message);
      return null;
    }
  };

  const captureHighQuality = async () => {
    const node = imageRef.current;
    if (!node) throw new Error("Preview node not ready");

    const scale = 2;
    const style = {
      transform: "scale(" + scale + ")",
      transformOrigin: "top left",
      width: node.offsetWidth + "px",
      height: node.offsetHeight + "px",
    };

    return domtoimage.toJpeg(node, {
      quality: 0.95,
      cacheBust: true,
      style,
      width: node.offsetWidth * scale,
      height: node.offsetHeight * scale,
    });
  };

  const handleGenerateImage = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError("");
    setImageGenerated(false);
    setPreviewImage(null);

    const id = await ensureTransactionId();
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      const dataUrl = await captureHighQuality();
      setPreviewImage(dataUrl);
      setImageGenerated(true);
    } catch (err) {
      console.error(err);
      setError("Image generation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!validateForm() || !imageGenerated) return;

    setLoading(true);
    let imageUrl = "";

    try {
      const finalId = await ensureTransactionId();
      if (!finalId) throw new Error("Failed to generate transaction ID.");

      const dataUrl = await captureHighQuality();
      setPreviewImage(dataUrl);

      // Upload image to server
      const uploadRes = await axios.post("/api/upload_image.php", {
        image: dataUrl,
        filename: `${finalId}.png`,
      });
      if (!uploadRes.data.success) throw new Error("Upload failed.");
      imageUrl = uploadRes.data.url;

      // Save transaction - keys aligned with DB/api
      const saveRes = await axios.post("/api/create_transaction.php", {
        creditor_id: selectedCreditor.id,
        debitor_id: selectedDebitor.id,
        amount: Number(amount),
        mode_of_transfer: modeOfTransfer || null,
        bank_name: bankName,
        branch_name: branch,
        ifsc_code: ifscCode.trim().toUpperCase(),
        account_holder_name: accountHolderName,
        account_number: accountNumber.trim(),
        status: "Committed",
        generated_image: imageUrl,
        parent_transaction_id: parentTransactionId,
        custom_transaction_id: finalId,
      });

      if (!saveRes.data.success)
        throw new Error(saveRes.data.message || "Save failed.");

      if (onTransactionSubmit) onTransactionSubmit();
      setPreviewImage(null);
      if (onClose) onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save transaction.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ac-container">
      <h2 className="ac-title">Generate Transaction Image</h2>

      {error && <div className="ac-alert ac-alert-danger">{error}</div>}

      {/* Hidden capture node for dom-to-image */}
      <div
        ref={imageRef}
        style={{
          padding: 15,
          backgroundColor: "#fff",
          border: "4px solid orange",
          borderRadius: 12,
          textAlign: "center",
          fontFamily: "Arial",
          position: "absolute",
          top: -9999,
          left: -9999,
          width: "fit-content",
        }}
      >
        <img
          src="/arihant_logo.bd6c233c_1.png"
          alt="Logo"
          style={{
            position: "absolute",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            width: 120,
            opacity: 0.08,
          }}
        />
        {qrCode && (
          <img
            src={qrCode}
            alt="QR"
            style={{
              display: "block",
              margin: "0 auto 10px auto",
              maxWidth: "100%",
            }}
          />
        )}
        <div style={{ fontSize: 26, fontWeight: "bold", marginTop: 10 }}>
          {customTransactionId || "TX-ID-PENDING"}
        </div>
        <div style={{ fontSize: 22, margin: "8px 0" }}>
          {selectedCreditor?.code || ""} – {selectedDebitor?.code || ""}
        </div>
        <div style={{ fontSize: 32, fontWeight: "bold", marginBottom: 8 }}>
          ₹ {parseFloat(amount || 0).toFixed(2)}
        </div>
        <div style={{ fontSize: 18 }}>
          {new Date().toLocaleDateString("en-IN")}
        </div>
      </div>

      {/* Step 1: QR / Cheque choice */}
      {hasQrCode === null ? (
        <div className="ac-section">
          <h4>Do you have a Cheque / QR Image?</h4>
          <div className="d-flex" style={{ gap: "0.75rem" }}>
            <button
              className="btn btn-success"
              type="button"
              onClick={() => setHasQrCode(true)}
            >
              Yes
            </button>
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => setHasQrCode(false)}
            >
              No
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="ac-section">
            <button
              className="btn btn-secondary mb-3"
              type="button"
              onClick={() => setHasQrCode(null)}
            >
              ⬅ Back
            </button>

            <div className="ac-grid">
              {/* Creditor */}
              <div className="ac-field">
                <label>Creditor</label>
                <select
                  className="form-control"
                  value={creditor}
                  onChange={(e) => setCreditor(e.target.value)}
                >
                  <option value="">Select Creditor</option>
                  {creditors.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Debitor */}
              <div className="ac-field">
                <label>Debitor</label>
                <select
                  className="form-control"
                  value={debitor}
                  onChange={(e) => setDebitor(e.target.value)}
                >
                  <option value="">Select Debitor</option>
                  {debitors.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div className="ac-field">
                <label>Amount</label>
                <input
                  type="number"
                  className="form-control"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>

            {/* QR / Mode of Transfer */}
            <div className="ac-grid" style={{ marginTop: "1rem" }}>
              {hasQrCode && (
                <div className="ac-field">
                  <label>Upload QR / Cheque Image</label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={handleQrCodeUpload}
                  />
                </div>
              )}

              {!hasQrCode && (
                <div className="ac-field">
                  <label>Mode of Transfer</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. NEFT, RTGS, UPI"
                    value={modeOfTransfer}
                    onChange={(e) => setModeOfTransfer(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Bank Details */}
          <div className="ac-section">
            <h4>Bank Details</h4>
            <div className="ac-grid">
              <div className="ac-field">
                <label>Bank Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                />
              </div>
              <div className="ac-field">
                <label>Branch</label>
                <input
                  type="text"
                  className="form-control"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                />
              </div>
              <div className="ac-field">
                <label>IFSC Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                />
              </div>
              <div className="ac-field">
                <label>Account Holder Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={accountHolderName}
                  onChange={(e) =>
                    setAccountHolderName(e.target.value)
                  }
                />
              </div>
              <div className="ac-field">
                <label>Account Number</label>
                <input
                  type="text"
                  className="form-control"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </div>
            </div>

            <button
              className="ac-btn mt-3"
              type="button"
              disabled={loading}
              onClick={handleGenerateImage}
            >
              {loading ? "Generating..." : "Generate Image"}
            </button>
          </div>
        </>
      )}

      {/* Preview */}
      {previewImage && (
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <h3>Preview</h3>
          <img
            src={previewImage}
            style={{ maxWidth: 300, border: "4px solid #333" }}
            alt="Preview"
          />
          <button
            className="btn btn-success mt-3"
            type="button"
            disabled={loading}
            onClick={handleConfirm}
          >
            Confirm & Add to Table
          </button>
        </div>
      )}
    </div>
  );
};

export default GenerateImage;